import React, { useState, useRef, useEffect } from "react";
import Text from "./Text";
import i18n from "@/i18n/config";
import { MdLanguage } from "react-icons/md";

// 言語の型定義: "ja" (日本語) または "en" (英語)
export type Language = "ja" | "en";

// Propsの型定義: 親コンポーネントから渡されるコールバック関数
// onLanguageChange: 言語が変更されたときに呼び出される関数 (任意)
type Props = {
  className?: string;
  onLanguageChange?: (language: Language) => void;
  resetKey?: number;
  isMobile?: boolean;
};

// 言語選択コンポーネント
const LanguageSelector: React.FC<Props> = ({
  className,
  onLanguageChange,
  resetKey,
  isMobile = false,
}) => {
  // ドロップダウンの開閉状態を管理するstate
  const [isOpen, setIsOpen] = useState(false);

  // コンポーネント全体を参照するためのref
  // 外側クリックを検知するために使用
  const wrapperRef = useRef<HTMLDivElement>(null);

  // ドロップダウンの開閉を切り替える関数
  const handleToggle = () => setIsOpen(!isOpen);

  // 言語が選択されたときの処理
  // - ドロップダウンを閉じる
  // - 親コンポーネントに選択された言語を通知
  const handleSelect = (language: Language) => {
    setIsOpen(false);
    i18n.changeLanguage(language);
    onLanguageChange?.(language);
  };

  // 外側クリックを検知してドロップダウンを閉じる処理
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // wrapperRefが現在のDOM要素を参照しており、
      // クリックされた場所がその外側である場合にドロップダウンを閉じる
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // mousedownイベントリスナーを登録
    document.addEventListener("mousedown", handleClickOutside);

    // コンポーネントのアンマウント時にリスナーを解除
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // 空の依存配列: マウント時に一度だけ実行

  // キオスクリセット時にドロップダウンを閉じて日本語に戻す
  useEffect(() => {
    if (!resetKey) return;
    setIsOpen(false);
    i18n.changeLanguage("ja");
  }, [resetKey]);

  const dropdownRootClasses = isMobile
    ? "relative inline-flex flex-col items-end"
    : "relative inline-flex flex-col";
  const dropdownWidthClasses = isMobile ? "w-[104px]" : "w-full";

  const buttonClasses = isMobile
    ? [
        "inline-flex",
        "h-[72px]",
        "w-[104px]",
        "p-3",
        "flex-col",
        "items-center",
        "justify-center",
        "gap-1",
        "bg-white",
        "cursor-pointer",
        "transition-colors",
        "duration-200",
        "ease-in-out",
        "hover:bg-Background",
        "active:bg-Border",
      ].join(" ")
    : [
        "inline-flex",
        "min-h-[44px]",
        "px-4",
        "py-2",
        "items-center",
        "justify-center",
        "bg-Surface",
        "cursor-pointer",
        "transition-colors",
        "duration-200",
        "ease-in-out",
        "hover:bg-Background",
        "active:bg-Border",
      ].join(" ");

  const dropdownItemClasses = [
    dropdownWidthClasses,
    isMobile ? "h-[72px]" : "min-h-[44px]",
    "px-4 py-2",
    "bg-white",
    "box-border",
    "flex justify-center items-center",
    "gap-1",
    "hover:bg-Background",
    "active:bg-Border",
    "cursor-pointer",
  ].join(" ");

  return (
    <div ref={wrapperRef} className={className}>
      <div className={dropdownRootClasses}>
        <button
          className={`${buttonClasses} border border-Border ${
            isOpen ? "rounded-t-2xl rounded-b-none" : "rounded-2xl"
          }`}
          onClick={handleToggle}
          type="button"
          aria-label="Language"
        >
          {isMobile && <MdLanguage className="text-[24px] text-TextPrimary" />}
          <Text
            as="span"
            variant="body/s"
            weight="bold"
            className="text-[10px] leading-none tracking-[0.14em]"
          >
            {isMobile ? "LANG" : "LANGUAGE"}
          </Text>
        </button>

        <div
          className={`absolute left-0 right-0 top-full -mt-px origin-top-right transition-all duration-200 ease-out ${
            isOpen
              ? "pointer-events-auto opacity-100 scale-y-100"
              : "pointer-events-none opacity-0 scale-y-95"
          }`}
        >
          <div
            className={`${dropdownWidthClasses} overflow-hidden rounded-b-2xl border border-Border bg-white`}
          >
            <button
              className={dropdownItemClasses}
              onClick={() => handleSelect("en")}
              type="button"
            >
              <Text as="span" variant="body/m" weight="bold">
                ENGLISH
              </Text>
            </button>
            <button
              className={`${dropdownItemClasses} border-t border-Border`}
              onClick={() => handleSelect("ja")}
              type="button"
            >
              <Text as="span" variant="body/m" weight="bold">
                日本語
              </Text>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector;
